import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {AbstractControl, FormArray, FormGroup, Validators} from '@angular/forms';
import { DateTime } from 'luxon';
import {PaymentTerm} from '../models/payment-term.model';
import {MultiPaymentTermsService} from '../multi-payment-terms.service';
import { OrdinalDatePipe } from '../../shared/pipes/ordinal-date.pipe';
import {PaymentPlanPreview} from '../models/payment-plan-preview.model';
import {Subscription} from 'rxjs';
import {Payment} from '../models/payment.model';
import {ErrorTriggers} from '../../payment-terms-selector/models/error-triggers.model';
import { SeFeThemesLoader } from 'se-fe-themes';

@Component({
  selector: 'app-payment-terms-preview',
  templateUrl: './payment-terms-preview.component.html',
  styleUrls: ['./payment-terms-preview.component.scss'],
  providers: [ OrdinalDatePipe ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PaymentTermsPreviewComponent implements OnInit, OnDestroy {
  @Input() isInvoice: boolean;
  @Input() preSelectedTerm: PaymentTerm[];
  @Input() paymentTermForm: FormGroup;
  @Input() applied: boolean;
  @Input() fullAmountUpfront: boolean;
  selectedValues: PaymentTerm[];
  datePrefix = 'customDates-';
  initialAmount = '0.00';
  previewPercentages: number[];
  previewData: PaymentPlanPreview[];
  selectedPaymentTermSubscription: Subscription;
  upfrontPaymentSubscription: Subscription;
  triggerErrorSubscription: Subscription;
  excludeStartDate: string;
  excludeEndDate: string;
  constructor(private multiPaymentTermsService: MultiPaymentTermsService,
              private ordinal: OrdinalDatePipe,
              private cdr: ChangeDetectorRef) { 
                SeFeThemesLoader.load({
                  supportedThemes: 'light',
                  url: 'https://se-fe-angular-services.ui.sportngin.com'
                });
              }

  ngOnInit(): void {
    const date: DateTime = DateTime.now();
    this.excludeStartDate = DateTime.fromISO(`${date.year}-01-01`).toFormat('yyyy-MM-dd');
    this.excludeEndDate = date.toFormat('yyyy-MM-dd');
    this.selectedPaymentTermSubscription = this.multiPaymentTermsService.selectedPaymentTermSubject
      .subscribe((paymentTerms: PaymentTerm[]) => {
        this.selectedValues = paymentTerms;
        this.cdr.detectChanges();
        this.triggerErrorSubscription = this.multiPaymentTermsService.$triggerError
          .subscribe((triggers: ErrorTriggers) => {
            if (triggers.triggerError) {
              this.selectedValues.forEach(value => {
                const customDates = this.customDatesControl(value.id);
                if (customDates && value.payment_term_strategy.strategy_type === 'custom_date' && customDates.invalid) {
                  customDates.controls.forEach((childControl: FormGroup) => {
                    childControl.controls.installment.markAsDirty();
                    childControl.controls.installment.markAsTouched();
                    childControl.controls.installment.updateValueAndValidity({emitEvent: false});
                  });
                }
              });
            }
          });
    });
    this.upfrontPaymentSubscription = this.multiPaymentTermsService.$upfrontPayment
      .subscribe((upfrontAmount: any) => {
        if (typeof upfrontAmount === 'string' && this.initialAmount !== upfrontAmount) {
          this.initialAmount = upfrontAmount;
        }
      });
  }

  ngOnDestroy(): void {
    this.selectedPaymentTermSubscription.unsubscribe();
    this.upfrontPaymentSubscription.unsubscribe();
    this.triggerErrorSubscription.unsubscribe();
  }

  getInstallments(payments: Payment[]): Payment[] {
    if (!payments) {
      return [];
    }
    const installments = payments.filter(payment => {
      const isUpfrontPayment = this.isUpfrontPayment(payment.upfront_payment);
      if (!isUpfrontPayment || this.fullAmountUpfront) {
        return true;
      }
      return false;
    });
    return installments;
  }

  getTotalAmount(payments: Payment[]): any {
    return payments.reduce((total, payment) => total + Number(payment.amount), 0);
  }

  getUpFrontAmount(payments: Payment[]): number {
    const upfrontAmount = payments.filter(payment => this.isUpfrontPayment(payment.upfront_payment));
    return upfrontAmount.length > 0 ? Number(upfrontAmount[0].amount) : 0;
  }

  private isUpfrontPayment(value: unknown): boolean {
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true';
    }
    return !!value;
  }

  removeAccordion(value: any): void {
    if (value.payment_term_strategy.strategy_type === 'custom_date') {
      this.paymentTermForm.removeControl(this.datePrefix + String(value.id));
    }
    this.selectedValues = this.selectedValues.filter(item => item.name !== value.name);
    this.multiPaymentTermsService.selectedPaymentTermSubject.next(this.selectedValues);
    this.multiPaymentTermsService.emitOutput.next(true);
  }

  dueDateParams(term: PaymentTerm): any {
    return {
      interval: term.payment_term_strategy.interval,
      ordinalDate: this.ordinal.transform(term.payment_term_strategy.day_of_month)
    };
  }

  getControlErrors(term: PaymentTerm): boolean {
    const customDates = this.customDatesControl(term.id);
    if (!customDates || !customDates.controls) {
      return false;
    }
    if (term.payment_term_strategy.strategy_type !== 'custom_date') {
      return false;
    }
    for (const control of customDates.controls) {
      const childControl = control as FormGroup;
      const installmentControl = childControl.controls.installment;
      if (!installmentControl) {
        continue;
      }
      if (!installmentControl.touched && !installmentControl.dirty && installmentControl.errors?.required) {
        continue;
      }
      if (installmentControl.touched && installmentControl.dirty && installmentControl.errors) {
        return true;
      }
    }
    return false;
  }

  customDatesControl(id: Number): FormArray {
    return this.paymentTermForm.get(this.datePrefix + id) as FormArray;
  }

  installmentDateControl(): AbstractControl {
    return this.f.installment;
  }

  getErrors(control: AbstractControl[], index: number): any {
    const previousDate = index === 0 ? '' : control[index - 1].value.installment;
    const currentDate = control[index].value.installment;
    const nextDate = (index === control.length - 1) ? '' : control[index + 1].value.installment;
    return {
      required: 'date is required',
      dateInvalid: 'date is invalid',
      dateOutOfRange: `Cannot use today’s date and the earliest date is tomorrow`,
      pastDate: 'No Past Dates are allowed',
      compareDate: 'Dates must be at least one (1) day apart ' + currentDate,
      nextDate: 'Enter a date after ' + previousDate
    };
  }

  get f(): { [key: string]: AbstractControl} {
    return this.paymentTermForm.controls;
  }
}
